import { useState, useEffect, useCallback } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Switch,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';

type Provider = 'sendgrid' | 'mailgun' | 'resend' | null;

interface Row {
  id: string;
  template_name: string;
  display_name: string;
  category: 'customer' | 'admin';
  recipient_type: string;
  provider: Provider;
  enabled: boolean;
  updated_at: string;
  updated_by: string | null;
}

interface RoutingState {
  default_provider: 'sendgrid' | 'mailgun' | 'resend';
  rows: Row[];
  providers_available: Array<'sendgrid' | 'mailgun' | 'resend'>;
}

type Language = 'en' | 'zh-TW' | 'zh-CN';
const LANGUAGE_OPTIONS: ReadonlyArray<{ value: Language; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'zh-TW', label: '繁體中文 (Traditional Chinese)' },
  { value: 'zh-CN', label: '简体中文 (Simplified Chinese)' },
];
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Implementation status of each template's email channel in production.
// 'live'            — actually dispatched as email by production code
// 'not_implemented' — registered in DB + has email template, but no production code path sends it yet
//
// Note: templates that production sends via a non-email channel (push/SMS)
// were removed from backend_email_provider_routing by migration 189, so they
// no longer appear on this page at all.
type ImplementationStatus = 'live' | 'not_implemented';

interface StatusInfo {
  status: ImplementationStatus;
  noteKey: string; // i18n key for the tooltip / extra-detail text
}

const TEMPLATE_STATUS: Record<string, StatusInfo> = {
  // Live email flows — no badge needed
  charging_receipt: { status: 'live', noteKey: '' },
  payment_settlement_error: { status: 'live', noteKey: '' },
  'account-deletion-completed': { status: 'live', noteKey: '' },

  // Templates registered but no production trigger code wired up yet
  promotional_offer: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  scheduled_maintenance: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  service_update: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  payment_refund_processed: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  charger_issues: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  charger_back_online: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  system_alert: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
  daily_summary: { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' },
};

function getTemplateStatus(templateName: string): StatusInfo {
  return TEMPLATE_STATUS[templateName] ?? { status: 'not_implemented', noteKey: 'emailRouting.status.notImplementedNote' };
}

interface TestSendDialogState {
  open: boolean;
  row: Row | null;
  email: string;
  language: Language;
  sending: boolean;
  error: string | null;
}

const INITIAL_TEST_DIALOG: TestSendDialogState = {
  open: false,
  row: null,
  email: '',
  language: 'en',
  sending: false,
  error: null,
};

export const EmailProviderRoutingPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [state, setState] = useState<RoutingState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testDialog, setTestDialog] = useState<TestSendDialogState>(INITIAL_TEST_DIALOG);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<RoutingState>(
        API_CONFIG.endpoints.backend.emailProviderRouting,
      );
      setState(data);
      setDirty(false);
    } catch (err: any) {
      notify(err?.message || translate('emailRouting.loadFailed'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!state || loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#009A75' }} />
      </Box>
    );
  }

  const setDefault = (v: 'sendgrid' | 'mailgun' | 'resend') => {
    setState({ ...state, default_provider: v });
    setDirty(true);
  };
  const setRowProvider = (tn: string, provider: Provider) => {
    setState({
      ...state,
      rows: state.rows.map((r) => (r.template_name === tn ? { ...r, provider } : r)),
    });
    setDirty(true);
  };
  const setRowEnabled = (tn: string, enabled: boolean) => {
    setState({
      ...state,
      rows: state.rows.map((r) => (r.template_name === tn ? { ...r, enabled } : r)),
    });
    setDirty(true);
  };

  const openTestDialog = (row: Row) => {
    setTestDialog({ ...INITIAL_TEST_DIALOG, open: true, row });
  };
  const closeTestDialog = () => {
    if (testDialog.sending) return;
    setTestDialog(INITIAL_TEST_DIALOG);
  };
  const sendTestEmail = async () => {
    if (!testDialog.row) return;
    if (!EMAIL_RX.test(testDialog.email.trim())) {
      setTestDialog((s) => ({ ...s, error: translate('emailRouting.test.invalidEmail') }));
      return;
    }
    setTestDialog((s) => ({ ...s, sending: true, error: null }));
    try {
      await apiClient.post(
        `${API_CONFIG.endpoints.backend.emailProviderRouting}/test-send`,
        {
          templateName: testDialog.row.template_name,
          recipientEmail: testDialog.email.trim(),
          language: testDialog.language,
        },
      );
      notify(
        translate('emailRouting.test.sendSucceeded', { email: testDialog.email.trim() }),
        { type: 'success' },
      );
      setTestDialog(INITIAL_TEST_DIALOG);
    } catch (err: any) {
      const msg = err?.message || translate('emailRouting.test.sendFailed');
      setTestDialog((s) => ({ ...s, sending: false, error: msg }));
    }
  };

  const save = async () => {
    if (!state) return;
    setSaving(true);
    try {
      const next = await apiClient.put<RoutingState>(
        API_CONFIG.endpoints.backend.emailProviderRouting,
        {
          default_provider: state.default_provider,
          rows: state.rows.map((r) => ({
            template_name: r.template_name,
            provider: r.provider,
            enabled: r.enabled,
          })),
        },
      );
      setState(next);
      setDirty(false);
      notify(translate('emailRouting.saveSucceeded'), { type: 'success' });
    } catch (err: any) {
      notify(err?.message || translate('emailRouting.saveFailed'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const mailgunAvailable = state.providers_available.includes('mailgun');
  const resendAvailable = state.providers_available.includes('resend');

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Title title={translate('emailRouting.pageTitle')} />
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('emailRouting.pageTitle')}
      </Typography>

      {!mailgunAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('emailRouting.mailgunNotConfigured')}
        </Alert>
      )}

      {!resendAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('emailRouting.resendNotConfigured')}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ minWidth: 200 }}>
            {translate('emailRouting.defaultProvider')}
          </Typography>
          <Select
            size="small"
            value={state.default_provider}
            onChange={(e) => setDefault(e.target.value as 'sendgrid' | 'mailgun' | 'resend')}
          >
            <MenuItem value="sendgrid">SendGrid</MenuItem>
            <MenuItem value="mailgun" disabled={!mailgunAvailable}>
              Mailgun
            </MenuItem>
            <MenuItem value="resend" disabled={!resendAvailable}>
              Resend
            </MenuItem>
          </Select>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {translate('emailRouting.defaultProviderHelp')}
          </Typography>
        </Box>
      </Paper>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('emailRouting.col.template')}</TableCell>
              <TableCell>{translate('emailRouting.col.category')}</TableCell>
              <TableCell>{translate('emailRouting.col.recipient')}</TableCell>
              <TableCell>{translate('emailRouting.col.provider')}</TableCell>
              <TableCell>{translate('emailRouting.col.enabled')}</TableCell>
              <TableCell>{translate('emailRouting.col.updated')}</TableCell>
              <TableCell align="right">{translate('emailRouting.col.test')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.rows.map((row) => {
              const statusInfo = getTemplateStatus(row.template_name);
              const showStatusBadge = statusInfo.status !== 'live';
              return (
              <TableRow key={row.template_name}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {row.display_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {row.template_name}
                  </Typography>
                  {showStatusBadge && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={translate('emailRouting.status.notImplementedBadge')}
                        title={translate(statusInfo.noteKey)}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          bgcolor: '#eef2f0',
                          color: '#64748b',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      row.category === 'customer'
                        ? translate('emailRouting.customer')
                        : translate('emailRouting.admin')
                    }
                  />
                </TableCell>
                <TableCell>{row.recipient_type}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={row.provider ?? ''}
                    displayEmpty
                    onChange={(e) =>
                      setRowProvider(
                        row.template_name,
                        (e.target.value || null) as Provider,
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>
                        {translate('emailRouting.useDefault', {
                          default: state.default_provider,
                        })}
                      </em>
                    </MenuItem>
                    <MenuItem value="sendgrid">SendGrid</MenuItem>
                    <MenuItem value="mailgun" disabled={!mailgunAvailable}>
                      Mailgun
                    </MenuItem>
                    <MenuItem value="resend" disabled={!resendAvailable}>
                      Resend
                    </MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={row.enabled}
                    onChange={(e) => setRowEnabled(row.template_name, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(row.updated_at).toLocaleString()}
                  </Typography>
                  {row.updated_by && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {row.updated_by}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SendIcon fontSize="small" />}
                    onClick={() => openTestDialog(row)}
                    disabled={!row.enabled}
                    title={
                      !row.enabled
                        ? translate('emailRouting.test.disabledHint')
                        : translate('emailRouting.test.button')
                    }
                  >
                    {translate('emailRouting.test.button')}
                  </Button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button variant="contained" disabled={!dirty || saving} onClick={save}>
          {saving ? translate('emailRouting.saving') : translate('emailRouting.saveAll')}
        </Button>
        <Button variant="outlined" disabled={!dirty || saving} onClick={load}>
          {translate('emailRouting.discard')}
        </Button>
      </Box>

      <Dialog open={testDialog.open} onClose={closeTestDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {translate('emailRouting.test.dialogTitle')}
          {testDialog.row && (
            <Typography variant="caption" display="block" color="text.secondary">
              {testDialog.row.display_name} ({testDialog.row.template_name})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {translate('emailRouting.test.helperText')}
            </Alert>
            <TextField
              label={translate('emailRouting.test.recipientEmail')}
              type="email"
              size="small"
              fullWidth
              autoFocus
              value={testDialog.email}
              onChange={(e) =>
                setTestDialog((s) => ({ ...s, email: e.target.value, error: null }))
              }
              disabled={testDialog.sending}
              placeholder="you@example.com"
            />
            <FormControl size="small" fullWidth disabled={testDialog.sending}>
              <InputLabel>{translate('emailRouting.test.language')}</InputLabel>
              <Select
                label={translate('emailRouting.test.language')}
                value={testDialog.language}
                onChange={(e) =>
                  setTestDialog((s) => ({ ...s, language: e.target.value as Language }))
                }
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {testDialog.error && <Alert severity="error">{testDialog.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTestDialog} disabled={testDialog.sending}>
            {translate('emailRouting.test.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={
              testDialog.sending ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
            disabled={testDialog.sending || !testDialog.email}
            onClick={sendTestEmail}
          >
            {testDialog.sending
              ? translate('emailRouting.test.sending')
              : translate('emailRouting.test.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
